import { Link } from "react-router-dom";
import { NAV_LINKS, CONTACT, BRAND } from "../data/content";
import { useLang } from "../i18n";
import { PhoneLinks } from "./PhoneLinks";
import { Logo } from "./Logo";
import Flagify, { Flag } from "./Flagify";
import { MARKET_SLUGS, marketPath } from "../data/markets";
import { Linkedin } from "lucide-react";

const FOOTER_EXTRA = [
  { id: "employers", path: "/for-employers" },
  { id: "candidates", path: "/for-candidates" },
  { id: "roles", path: "/open-roles" },
  { id: "industries", path: "/industries" },
  { id: "process", path: "/how-it-works" },
];

const FOOTER_SERVICES = [  { id: "workforce", path: "/workforce" },
  { id: "hse", path: "/hse" },
  { id: "manpower", path: "/services" },
  { id: "partners", path: "/about#partners" },
];

const XIcon = () => (  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
    <path d="M18.9 2H22l-6.9 7.9L23 22h-6.4l-5-6.6L5.8 22H2.7l7.4-8.4L1.6 2H8l4.7 6.2L18.9 2Zm-1.1 18h1.7L7.4 3.8H5.6L17.8 20Z" />
  </svg>
);

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="border-t border-[#DDE4EA] glass-section" data-testid="site-footer">
      <div className="mx-auto max-w-[1760px] px-5 pb-28 pt-14 sm:px-8 sm:pt-16 lg:pb-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <Logo size="footer" />
            <p className="mt-4 font-display text-[13px] font-bold uppercase tracking-[0.14em] text-[#0F5C8C]" data-testid="footer-positioning">{t.footer.positioning}</p>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#41535F]" data-testid="footer-blurb"><Flagify text={t.footer.blurb} /></p>
          </div>
          <div>
            <h3 className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#5E7280]">{t.footer.explore}</h3>
            <ul className="mt-5 space-y-2.5">
              {NAV_LINKS.flatMap((l) => (l.children ? l.children : [l])).map((l) => (
                <li key={l.id}>
                  <Link to={l.path} data-testid={`footer-nav-${l.id}-link`} className="inline-block py-1 text-sm text-[#41535F] transition-colors hover:text-[#1477B3]">
                    {t.nav[l.id]}
                  </Link>
                </li>
              ))}
              {FOOTER_EXTRA.map((l) => (
                <li key={l.id}>
                  <Link to={l.path} data-testid={`footer-nav-${l.id}-link`} className="inline-block py-1 text-sm text-[#41535F] transition-colors hover:text-[#1477B3]">
                    {t.nav[l.id]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#5E7280]">{t.footer.services}</h3>
            <ul className="mt-5 space-y-2.5">
              {FOOTER_SERVICES.map((s) => (
                <li key={s.id}>
                  <Link to={s.path} data-testid={`footer-service-${s.id}-link`} className="inline-block py-1 text-sm text-[#41535F] transition-colors hover:text-[#1477B3]">
                    {t.nav[s.id]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#5E7280]">{t.markets.label}</h3>
            <ul className="mt-5 space-y-2" data-testid="footer-markets">
              {Object.keys(MARKET_SLUGS).map((code) => (
                <li key={code}>
                  <Link to={marketPath(code)} data-testid={`footer-market-${code}-link`} className="inline-flex items-center gap-2 py-1 text-sm text-[#41535F] transition-colors hover:text-[#1477B3]">
                    <Flag code={code} /> {t.markets.names[code]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#5E7280]">{t.footer.contact}</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-[#41535F]">
              <li data-testid="footer-email">
                <span className="block text-[10px] uppercase tracking-[0.15em] text-[#5E7280]">{t.footer.general}</span>
                <a href={`mailto:${CONTACT.email}`} className="transition-colors hover:text-[#1477B3]">{CONTACT.email}</a>
              </li>
              <li><PhoneLinks prefix="footer-phone" /></li>
              <li data-testid="footer-careers">
                <span className="block text-[10px] uppercase tracking-[0.15em] text-[#5E7280]">{t.footer.careers}</span>
                <a href={`mailto:${CONTACT.hrEmail}`} className="transition-colors hover:text-[#1477B3]">{CONTACT.hrEmail}</a>
              </li>
              <li data-testid="footer-billing">
                <span className="block text-[10px] uppercase tracking-[0.15em] text-[#5E7280]">{t.footer.billing}</span>
                <a href={`mailto:${CONTACT.billingEmail}`} className="transition-colors hover:text-[#1477B3]">{CONTACT.billingEmail}</a>
              </li>
              <li data-testid="footer-address">
                {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
              </li>
              <li data-testid="footer-vat">{t.footer.vat} {CONTACT.orgNumber}</li>
              <li className="flex flex-wrap gap-2 pt-1">
                <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" className="inline-flex items-center gap-2 border border-[#C8D3DB] px-3 py-1.5 text-xs font-semibold text-[#0E1B24] transition-colors hover:border-[#0F5C8C] hover:text-[#0F5C8C]"><Linkedin size={14} /> LinkedIn</a>
                <a href={CONTACT.x} target="_blank" rel="noopener noreferrer" data-testid="footer-x" className="inline-flex items-center gap-2 border border-[#C8D3DB] px-3 py-1.5 text-xs font-semibold text-[#0E1B24] transition-colors hover:border-[#0F5C8C] hover:text-[#0F5C8C]"><XIcon /> X</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-4 border-t border-[#C8D3DB] pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-[#5E7280]">© {CONTACT.foundedYear} {BRAND.name}. {t.footer.rights}</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs text-[#5E7280] transition-colors hover:text-[#1477B3]">
              {t.footer.privacy}
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="text-xs text-[#5E7280] transition-colors hover:text-[#1477B3]">
              {t.footer.terms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
